import NegociatedList from "app/pages/FicheProduit/NegociatedList/NegociatedList";
import { FormattedMessage } from "react-intl";
import get from "lodash/get";
import UsefulInformation from "app/pages/FicheProduit/components/UsefulInformation/UsefulInformation";
import Collapse from "app/components/ui/collapse/Collapse";
import className from "classnames";
import Typography, { TYPOGRAPHY_VARIANTS } from "app/pages/.shared/Typography/Typography";
import { memo, useContext } from "react";
import PropTypes from "prop-types";
import USAWarningMessage from "./USAWarningMessage";
import IncludedItemsList from "./IncludedItemsList";
import SectionsList from "./SectionsList";
import "./HotelSections.scss";
import loadable from "@loadable/component";
import AdditionalSections from "app/pages/FicheProduit/components/AdditionalSections/AdditionalSections";
import AppGlobalsContext from "app/AppGlobalsContext";
import { SECONDARY_SECTION_TYPES } from "app/constants";
import { ArrowDownIcon } from "app/icons/navigation";
import { ICON_SIZES } from "app/constants";

const LocationSection = loadable(() => import("./LocationSection"));
const FacilitySection = loadable(() => import("./FacilitySection"));

const HotelSections = ({
	printMode,
	hotel = {},
	googleAPIKey = "",
	specialOfferInfos,
	warningMessage,
	handleProductDetailsExpand,
	facilityGroups,
}) => {
	const { partnerCode } = useContext(AppGlobalsContext);
	const isSV = partnerCode.startsWith("SV");

	const filteredSections = (hotel.sections?.secondary || []).filter(section => {
		const restaurantType = SECONDARY_SECTION_TYPES?.RESTAURANT ?? 1;
		return !(isSV && section.type === restaurantType);
	});

	filteredSections.forEach(section => {
		if (section?.type === SECONDARY_SECTION_TYPES?.RESTAURANT) {
			section.headline = <FormattedMessage id={"product.page.restaurant.headline"} />;
		}
		if (section?.type === SECONDARY_SECTION_TYPES?.ACTIVITY) {
			section.headline = <FormattedMessage id={"product.page.activities.label"} />;
		}
	});

	return (
		<>
			{printMode ? (
				<>
					<SectionsList sections={hotel.sections?.primary || []} />

					<FacilitySection facilities={facilityGroups} />
					<LocationSection hotel={hotel} googleAPIKey={googleAPIKey} />

					<SectionsList sections={filteredSections} />

					<NegociatedList
						sizeIcon={ICON_SIZES.MD}
						negociatedList={hotel.negotiated}
						specialOffer={specialOfferInfos}
					/>

					<div className="hotel-section__included">
						<div className="included">
							<IncludedItemsList
								titleId="price.detail.included.title"
								items={get(hotel, "included", [])}
							/>
							<IncludedItemsList
								titleId="price.detail.not.included.title"
								items={get(hotel, "notIncluded", [])}
							/>
						</div>
					</div>

					<UsefulInformation usefulInformations={hotel.usefulInformations} />
					<USAWarningMessage showWarning={warningMessage} />
				</>
			) : (
				<Collapse
					className={className({
						"hotel-section": true,
						"hotel-section__with-warning": warningMessage,
					})}
					expandIcon={({ isActive }) => (
						<ArrowDownIcon
							style={{
								transform: `rotate(${isActive ? 180 : 0}deg)`,
							}}
							className="icon"
						/>
					)}
				>
					{hotel.sections?.primary?.map(section => (
						<Collapse.Panel
							className="hotel-section__sections-item"
							panelKey={section.headline}
							header={
								<Typography
									variant={TYPOGRAPHY_VARIANTS.LARGE}
									isBold
									className="hotel-section__title"
									onClick={handleProductDetailsExpand}
								>
									{section.headline}
								</Typography>
							}
						>
							<AdditionalSections
								key={section.headline}
								headline={section.headline}
								blocks={section.blocks}
								moreDetails={section.moreDetails}
							/>
						</Collapse.Panel>
					))}

					{hotel.facilityGroups?.length > 0 && (
						<Collapse.Panel
							className="hotel-section__sections-item"
							header={
								<Typography
									variant={TYPOGRAPHY_VARIANTS.LARGE}
									isBold
									className="hotel-section__title"
									onClick={handleProductDetailsExpand}
								>
									<FormattedMessage id="sdp.hotel.description.facilities.label" />
								</Typography>
							}
						>
							<FacilitySection facilities={facilityGroups} />
						</Collapse.Panel>
					)}

					{filteredSections?.map(section => {
						return (
							<Collapse.Panel
								className="hotel-section__sections-item"
								panelKey={section.headline}
								header={
									<Typography
										variant={TYPOGRAPHY_VARIANTS.LARGE}
										isBold
										className="hotel-section__title"
										onClick={handleProductDetailsExpand}
									>
										{section.headline}
									</Typography>
								}
							>
								<AdditionalSections
									key={section.headline}
									headline={section.headline}
									blocks={section.blocks}
									moreDetails={section.moreDetails}
								/>
							</Collapse.Panel>
						);
					})}

					<USAWarningMessage showWarning={warningMessage} />
				</Collapse>
			)}
		</>
	);
};

HotelSections.propTypes = {
	printMode: PropTypes.bool,
	hotel: PropTypes.object,
	googleAPIKey: PropTypes.string,
	specialOfferInfos: PropTypes.object,
	warningMessage: PropTypes.bool,
	handleProductDetailsExpand: PropTypes.func,
	facilityGroups: PropTypes.array,
};

export default memo(HotelSections);
